import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { LatLngLiteral } from "leaflet";
import { useInternal } from "uikit";

import { Map } from "../../../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import orderPage from "../../../../../redux/reducers/OrdersPage";

function nearSearchBias(point: LatLngLiteral, radius: number) {
	return { point, radius };
}

/** Prefer non-empty `houses` from geocoder hit; otherwise keep prior list. */
function housesFromSearchHit(
	enriched: Map.Search.Object,
	base: Partial<Map.Search.Object>,
): Map.Search.Object["houses"] {
	if (Array.isArray(enriched.houses) && enriched.houses.length) {
		return enriched.houses;
	}
	return base.houses ?? [];
}

/** Keep full street house list but guarantee the user-picked number is present (for UI selection). */
function ensurePickedHouseInList(
	houses: Map.Search.Object["houses"] | undefined,
	data: Partial<Map.Search.Object>,
): Map.Search.Object["houses"] {
	const picked = String(
		(data as { number?: string }).number ??
			(data as { house?: string }).house ??
			"",
	).trim();
	if (!picked) return houses ?? [];
	const list = [...(houses ?? [])];
	const norm = (s: string) => s.trim().toLowerCase();
	const exists = list.some((h) => norm(h.number || "") === norm(picked));
	if (!exists) {
		list.unshift({
			id: String(data.id ?? ""),
			number: picked,
		});
	}
	return list;
}

const useGetCoordinatesForStreet: useGetCoordinatesForStreet.Props2 = () => {
	const dispatch = useTypedDispatch();
	const language = useTypedSelector((state) => state.session.language);
	const { searchRadiusMeters: searchRadius } = useTypedSelector(
		(state) => state.settings.map,
	);

	const [coordinates, setCoordinates] = useInternal<LatLngLiteral | null>(
		null,
	);
	const [response, setResponse] = useInternal<Map.Search.Object | null>(null);
	const [street, setStreet] = useInternal<string | null>(null);

	const buildStreetQuery = useCallback(
		(data: Partial<Map.Search.Object>, streetQuery: string) => {
			const settlement = data.settlement
				? `${data.settlement.trim()}, `
				: "";
			const settlementType = data.settlementType
				? `${data.settlementType.trim()} `
				: "";
			const street = streetQuery ? `${streetQuery.trim()}` : "";
			return `${settlementType}${settlement}${street}`.trim();
		},
		[],
	);

	const get = useCallback<useGetCoordinatesForStreet.Props["get"]>(
		async (data, streetQuery) => {
			const optionsType: Map.Search.Type[] = [
				"favorite",
				"localObject",
				"object",
			];

			if (
				data &&
				streetQuery &&
				data.placeId &&
				data.searchType === "google_geocode"
			) {
				setStreet(streetQuery);

				const searchOptions: Map.Search.Options = {
					language,
					searchType: "google_geocode",
					country: "ua",
					near: nearSearchBias(data.coordinates, searchRadius),
				};

				const result = await Map.search(
					String(data.placeId),
					searchOptions,
				);

				if (result.objects?.length) {
					const [streetData] = result.objects;

					if (streetData) {
						const updatedData: Map.Search.Object = {
							...data,
							coordinates: streetData.coordinates,
							id: streetData.id,
							placeId: streetData.placeId,
							houses: housesFromSearchHit(streetData, data),
							street: streetData.street || data.street,
							settlement:
								streetData.settlement || data.settlement,
							streetType:
								streetData.streetType || data.streetType,
							house:
								(streetData as any).house ||
								(data as any).house ||
								"",
							number:
								(streetData as any).number ||
								(data as any).number ||
								"",
						} as Map.Search.Object;

						setResponse(updatedData);
						setCoordinates(streetData.coordinates);
						return updatedData;
					}
					setResponse({
						...data,
						street: streetQuery,
					} as Map.Search.Object);
					return {
						...(data as any),
						street: streetQuery,
					} as Map.Search.Object;
				}
			}

			if (
				data &&
				streetQuery &&
				data.coordinates &&
				data.searchType === "osm_nominatim"
			) {
				const houseToken = String(
					(data as any).number ?? (data as any).house ?? "",
				).trim();

				// Для object/address у нас уже є все необхідне з саджеста:
				// точка (data.coordinates), номер будинку, назва вулиці. Дома вулиці
				// підтягувати не треба — юзер уже обрав конкретний номер. Йдемо
				// shortcut-ом без запиту на бек. Для street з номером (рідкісний
				// OSM-shape) — теж shortcut. Запит на /search потрібен ТІЛЬКИ для
				// клік по «голій» вулиці без номера, де треба весь список будинків.
				const useExistingOsmPoint =
					data.type === "object" ||
					data.type === "address" ||
					(Boolean(houseToken) && data.type === "street");

				if (useExistingOsmPoint) {
					const updatedData: Map.Search.Object = {
						...data,
						house:
							(data as any).house ||
							(data as any).number ||
							houseToken,
						number:
							(data as any).number ||
							(data as any).house ||
							houseToken,
					} as Map.Search.Object;
					setStreet(streetQuery);
					setResponse(updatedData);
					setCoordinates(updatedData.coordinates);
					return updatedData;
				}

				setStreet(streetQuery);
				const query = buildStreetQuery(data, streetQuery);
				const searchOptions: Map.Search.Options = {
					language,
					searchType: "street",
					country: "ua",
					limit: 1,
					near: nearSearchBias(data.coordinates, searchRadius),
					placeId: data.placeId,
				};

				const result = await Map.search(query, searchOptions);
				if (result.objects?.length) {
					const normalize = (s?: string) =>
						(s || "").trim().toLowerCase();
					const match =
						result.objects.find(
							(item) =>
								(item.placeId &&
									item.placeId === data.placeId) ||
								(normalize(item.street) ===
									normalize(streetQuery) &&
									normalize(item.settlement) ===
										normalize(data.settlement)),
						) || result.objects[0];

					if (match) {
						const isAddressPick = data.type === "address";
						const mergedHouses = housesFromSearchHit(match, data);
						const houses = isAddressPick
							? ensurePickedHouseInList(mergedHouses, data)
							: mergedHouses;

						const updatedData: Map.Search.Object = {
							...data,
							id: isAddressPick ? data.id : match.id || data.id,
							placeId: match.placeId || data.placeId,
							coordinates: isAddressPick
								? data.coordinates
								: match.coordinates || data.coordinates,
							houses,
							street: match.street || data.street || streetQuery,
							streetType:
								match.streetType || data.streetType || "",
							number: isAddressPick
								? (data as any).number ||
								  (data as any).house ||
								  houseToken ||
								  ""
								: (match as any).number ||
								  (data as any).number ||
								  "",
							house: isAddressPick
								? (data as any).house ||
								  (data as any).number ||
								  houseToken ||
								  ""
								: (match as any).house ||
								  (match as any).number ||
								  (data as any).house ||
								  (data as any).number ||
								  "",
						} as Map.Search.Object;
						setResponse(updatedData);
						setCoordinates(updatedData.coordinates);
						return updatedData;
					}
				}
			}

			if (data?.type && optionsType.includes(data.type) && !streetQuery) {
				setResponse(data as Map.Search.Object);
				setCoordinates(data.coordinates);
				return data as Map.Search.Object;
			}

			return null;
		},

		[
			language,
			searchRadius,
			setCoordinates,
			setStreet,
			setResponse,
			buildStreetQuery,
		],
	);

	useMemo(() => {
		if (coordinates) dispatch(orderPage.actions.setMapCenter(coordinates));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coordinates]);

	const reset = useCallback(() => {
		setResponse(null);
		setStreet(null);
	}, [setResponse, setStreet]);

	return useMemo<useGetCoordinatesForStreet.Props>(
		() => ({
			street,
			response,
			coordinates,
			reset,
			get,
			setStreet,
			setResponse,
		}),
		[street, response, coordinates, reset, get, setStreet, setResponse],
	);
};

declare namespace useGetCoordinatesForStreet {
	type Props2 = () => Props;
	interface SearchQuery {
		street?: string;
		streetType?: string;
		settlement?: string;
		settlementType?: string;
		district?: string;
	}

	interface Props {
		response: Map.Search.Object | null;
		setResponse: Dispatch<SetStateAction<Map.Search.Object | null>>;
		coordinates: LatLngLiteral | null;
		get: (
			data: Partial<Map.Search.Object> &
				SearchQuery & {
					coordinates: LatLngLiteral;
					placeId?: string;
				},
			streetQuery: string,
		) => Promise<Map.Search.Object | null>;
		reset: () => void;
		street: string | null;
		setStreet: Dispatch<SetStateAction<string | null>>;
	}
}

export default useGetCoordinatesForStreet;
